import type { Block } from '@/content/blocks'
import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { STORIES, STORIES_INDEX } from '@/content/stories'

/**
 * The Hall of Fame: what nineteen customers said, and the card template that
 * says it.
 *
 * **The imported page cannot be repaired in place, which is why this file
 * exists.** `imported/pages/en/testimonials.json` is the WordPress REST body of
 * digitopia.co/testimonials/, and the importer flattened a page of designed
 * quote blocks into a run of bare paragraphs — with every attribution welded to
 * the *front of the next person's quote*. Block four reads
 * "<strong>Tekin Gülşen, VP of IT Y-EMEA…</strong> Digitopia's Digital Maturity
 * Index has been a key element in structuring our digital transformation…",
 * which is Yazaki's name on TEMSA's words. All nineteen are offset by one, the
 * logos are gone, and the last two paragraphs are an attribution with no quote
 * left to attach to. Nothing about that is fixable by styling the blocks; the
 * pairing itself is wrong, so the page has been printing misattributed customer
 * quotes since the import.
 *
 * The correct text was supplied on 2026-08-27 with ten screenshots of the live
 * design — *"bu tasarım şeklini bizim hall of fame template i olarak
 * kaydedebilirsin… aşağıda sayfanın metni full şekilde var"* — so this file is
 * the text as the customer gave it, re-paired, and `pages/Testimonials.tsx` is
 * the template it renders in.
 *
 * Built like `content/stories.ts`, `content/reports.ts` and `content/ebooks.ts`,
 * which answered the same request for the other three index pages: a typed
 * catalogue plus a designed template, attached to the imported page in
 * `src/legacy.ts` at load time rather than written into the JSON — that JSON is
 * the record of what digitopia.co said, and this is what we are saying instead.
 *
 * **Order is the order they were given in**, which is the live page's own and is
 * neither alphabetical nor the wall's. `customerWall.ts` sorts because a wall of
 * 55 marks in any other order is a ranking nobody asked for; nineteen
 * hand-placed quotes *are* a marketing decision, and re-sorting them here would
 * quietly overrule it. The one exception is `LEAD` at the foot of this file —
 * four cards named explicitly on 2026-08-28 to open the page — which is the
 * marketing decision arriving rather than this file guessing at one; everything
 * not named in it keeps the position it has here.
 *
 * **English only.** The page exists in one locale — `LINKS.hallOfFame` points
 * every locale's menu at `/en/testimonials` — so there is no Turkish sibling to
 * keep in step, and these are quotations, which are not translated anyway.
 */
export interface Testimonial {
  /** The company, as the card's eyebrow reads it. */
  company: string
  /**
   * The mark, or nothing where we hold no usable file.
   *
   * Written out rather than resolved through `logoFor()`, because the two
   * catalogues answer different questions and the join would pick silently.
   *
   * **Every company that has a 300×300 `*card.png` takes it here** — fifteen of
   * the eighteen, changed on 2026-08-27 along with `fit="quote"`. That inverts
   * what this file used to say, and the reason it used to say the opposite went
   * away in the same change: the card drew its mark in a height-capped row like
   * the wall's, where a wordmark floating in a padded square renders about a
   * third the width of a tight crop beside it, so a company on the wall took its
   * wall crop. A quote card now draws every mark at **one width**, which is
   * exactly the sizing a padded canvas wants — the mark is the full width of the
   * square and the padding above and below it is transparent air.
   *
   * With shape no longer deciding, the two things that are left both point the
   * same way. The card set is transparent HD, and this is the one surface that
   * draws a mark straight onto grey (`bg-band`), where a crop with no alpha
   * prints a white rectangle — `netafim.webp` did, which is why Netafim moved
   * first, on its own, before the rest followed. And it is the newer, cleaner
   * artwork: LC Waikiki is the worked example, sent as HD transparent, wired
   * into the home page and the story cards, and left printing the old lossy
   * marketing crop here until this change.
   *
   * The three without a card square — CFN Kimya, Hopi and Kuveyt Türk — keep
   * their marketing crops, which are tight, alpha-carrying WebP and size to the
   * same width as everything else. Nothing to fix there.
   *
   * That is the general rule for this file, asked for on 2026-08-27: *"bir
   * görselin daha iyi ve kaliteli bir versiyonu yüklenince sitedeki öbür
   * yerlerde aynı görsel varsa ona da yansısın"*. A better file for a company
   * lands in `logo-src/` for one surface; every other surface that draws that
   * company has to be looked at in the same change. `logo-src/README.md` lists
   * which four they are.
   */
  logo?: { name: string; src: string }
  /**
   * The executive's portrait.
   *
   * Ten arrived on 2026-08-27 — *"hall of fame için görseller, görsel
   * isimlerinde kim oldukları yazıyor"* — which is what the card's right-hand
   * side was built around and had been standing empty since the template
   * shipped. **The remaining nine arrived the same day, on the same words**, so
   * all nineteen cards now carry one and the field is optional only because the
   * twentieth customer's photograph will arrive after their quote does.
   *
   * The person each file shows was read off the sender's own filenames, which
   * are people's names and not companies' — the pairing back to a card is
   * through `attribution`, so a portrait sits beside the words of the person in
   * it and nowhere else.
   *
   * They are black-and-white head-and-shoulders cut-outs **with a transparent
   * ground**, and the template draws them as exactly that: no disc, no fill,
   * standing on the card's bottom border (2026-08-27 — *"gri kutunun tam
   * altından denk gelecek şekilde çıksın insanlar ve transparan olsunlar"*).
   *
   * **All nineteen are 900×900**, in two batches on 2026-08-28 under the same
   * words both times (*"hall of fame görselleri 900 x 900"*): ten that morning,
   * and the nine that were still the original 300×300 that afternoon — TEMSA,
   * Kordsa, Akçansa, ebebek, LC Waikiki, Lila Kağıt, Petrol Ofisi, Borusan
   * Otomotiv Group and Arzum.
   *
   * **Both batches arrived under numbered filenames** (`11.png`…`19.png` for the
   * second), which carry no name at all, so each one was matched *face by face*
   * against the 300×300 file it replaces rather than by position in the list —
   * two of the nine confirm themselves, Aydın Şener and Mehmet Ali Gülşen wear
   * conference badges with their names on. Position would have been a guess, and
   * a guess is how this page came to print Yazaki's name over TEMSA's words in
   * the first place.
   *
   * That is what let the figures reach the height the reference screenshots
   * show. `pnpm images` never upscales and every card draws the same size box,
   * so the size the template can use is the size the *smallest* source carries:
   * 288px while all nineteen were 300×300, 384px on the strength of the first
   * ten, and 448px now — 896px on a 2× screen, four short of the sources. One
   * number in `pages/Testimonials.tsx`, as that file said it would be.
   *
   * `alt` is the person's name and nothing more. The role is on the line beside
   * the picture already, and a screen reader that reads it twice is reading the
   * card wrong.
   */
  portrait?: Photo
  /** The quote, one string per paragraph, exactly as it was given. */
  quote: string[]
  /** Who said it — name and role, on one line, as the reference sets it. */
  attribution: string
  /**
   * The customer story behind the quote, as a slug in `content/stories.ts`.
   *
   * The reference design puts a "Read the Full Story" pill under some quotes and
   * not others, and this is the honest version of that distinction: a card gets
   * the button when there is a page to open. Four of the nineteen — CFN Kimya,
   * Hopi, Kuveyt Türk and Epic Museum — have no story page, so they carry the
   * quote alone, which is what the reference shows for them too.
   */
  story?: string
  /**
   * Whether this quote also belongs on the Hall of Fame wall. Omitted means yes,
   * which is what every card here has always done.
   *
   * `false` says: this person's words were given for their own customer page and
   * nowhere else. Penta Teknoloji's second speaker is the first of those
   * (2026-09-05 — *"hall of fame kısmında olmasına gerek yok sadece penta
   * sayfasında ikinci seçenek olsun"*), so the flag lives beside the quote rather
   * than as a name in an exclusion list on this side, and deleting the file would
   * take the Penta page's second tab with it.
   */
  hallOfFame?: boolean
}

/**
 * The card template's own decoration: the gradient arc that goes behind the
 * person.
 *
 * The card first shipped reusing `photo-src/gradient-ribbon.png` — the blue-to-
 * pink curve the site already had — flipped, on the grounds that a nineteenth
 * copy of a curve we own is waste. On 2026-08-27 the ribbon itself was supplied:
 * *"hall of fame template inde kullanacağın ve insanların fotoğraflarının
 * arkasında olacak ribbon bu"*. So it is this file and not the reused one, and
 * it is **not** mirrored — it is drawn the way it is meant to sit, fading out at
 * the low left end where the quote is and falling solid down the card's right
 * edge behind the portrait.
 *
 * It is a flatter curve than the old one (1000×800 against 698×804), which is
 * why `pages/Testimonials.tsx` caps its height lower than it used to: the width
 * follows the height, and the same cap would have drawn this one into the words.
 */
export const TESTIMONIAL_RIBBON = '/photo/gradient-ribbon-pink-arc.png'

/**
 * What the cards are still missing, said once rather than nineteen times.
 *
 * It is one gap with one owner — a batch of files somebody has to send — so it
 * is one amber note above the cards and one entry in the build manifest, not a
 * note per card that would print the same paragraph down the whole page.
 */
export const TESTIMONIALS_CHECK =
  'All nineteen executive portraits are now on their cards at 900×900, so the figures have '
  + 'grown to 448px — the full height the design calls for, and as far as they can go '
  + 'without larger originals. The last nine to arrive (TEMSA, Kordsa, Akçansa, ebebek, LC '
  + 'Waikiki, Lila Kağıt, Petrol Ofisi, Borusan Otomotiv Group and Arzum) came under '
  + 'numbered filenames with no names in them, so each was matched by face against the '
  + 'smaller file it replaces; please confirm every face is beside the right quote before '
  + 'this goes to production, as that pairing is the one thing nothing here can verify. A '
  + 'twentieth card joined them on 2026-08-28 — Victoria Kouyoumjian of Intel AI, taken off the '
  + 'foot of the Intel AI story page rather than off this one, so it is the only card here that '
  + 'digitopia.co does not also show; it is appended at the end because the order above is the '
  + 'live page’s own, and it is worth confirming that is where you want it. Two '
  + 'other things are worth a glance down the page. The portraits are drawn as transparent '
  + 'cut-outs standing on the card’s bottom edge, so any file saved without an alpha channel '
  + 'shows as a white square. And Petrol Ofisi’s photograph is mirrored — the badge and '
  + 'lanyard text on it read backwards — which was invisible at the old size and is legible '
  + 'at this one; send an unflipped copy if it bothers you. The last logo gap closed on '
  + '2026-08-28 — Epic Museum now has its own teal wordmark on its card instead of just its '
  + 'name — but that file is a lossy WebP export, so if it shows as a white block on the '
  + 'card’s grey rather than sitting on it, send a transparent PNG and it will be swapped. '
  + 'One thing to confirm about the new order: the page was asked on 2026-08-28 to open on '
  + 'Litehouse Foods, Yazaki, LC Waikiki and Bridgestone, and the fourth of those has no card '
  + 'of its own — there is a Bridgestone story but no Bridgestone quote — so the fourth '
  + 'position is held by Brisa, the Bridgestone–Sabancı company and the only card from that '
  + 'group. If a separate Bridgestone quote was meant, send it and it will take that slot '
  + 'instead. Everything from the fifth card down is unchanged.'

/**
 * Every testimonial, one file each.
 *
 * Same reason as `content/stories.ts`: the AI editor writes a quote here every
 * time a customer page asks for the testimonial template — seven of the
 * twenty-two requests queued on 2026-08-28 did — and two runs editing one array
 * is a push conflict, not a merge. Add
 * `src/content/testimonials/<company>.ts`:
 *
 *     import type { Testimonial } from '../testimonials'
 *     export const added: Testimonial = { company: 'Acme', quote: [...], ... }
 *     export const order = 20
 *
 * The twenty that were written into this file by hand joined them on
 * 2026-08-30, the last of the four catalogues to be carried through. The glob
 * alone had fixed collisions and left the read cost where it was — and read
 * cost is `context × turns × cache-read`, which is 50–62% of what an edit bills.
 *
 * `order` then company sorts the wall, so it never depends on filesystem
 * iteration. **A file with no `order` sorts last**, not first: the default is
 * `Number.MAX_SAFE_INTEGER`, where it could be 0 while `CURATED` led the array
 * by construction. The four files that predate this carry no `order` and so keep
 * exactly the positions they had, after the twenty.
 */
const ADDED = import.meta.glob<{ added: Testimonial; order?: number }>(
  './testimonials/*.ts',
  { eager: true },
)

const IN_ORDER: Testimonial[] = Object.values(ADDED)
  .filter((m) => m.added)
  .sort(
    (a, b) =>
      (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
      || a.added.company.localeCompare(b.added.company),
  )
  .map((m) => m.added)

/**
 * The four cards the page opens on, named on 2026-08-28 (*"Hall of Fame'de en
 * üstten aşağıya doğru 1- Litehouse Foods 2- Yazaki 3- LC Waikiki 4-
 * Bridgestone, ver geri kalanı olduğu gibi olsun"*).
 *
 * It is a lead list rather than a re-cut `CURATED`, for two reasons. Litehouse
 * is one of the per-file testimonials, so it could not be first without pulling
 * a file's worth of copy back into the array the files exist to keep people out
 * of — and the file comment above says the array's order *is* the live page's,
 * which stays true of everything below the lead: the rest keep their positions
 * exactly, which is what "geri kalanı olduğu gibi" asked for.
 *
 * **"Bridgestone" is read as Brisa**, the only card from that group — Brisa is
 * the Bridgestone–Sabancı company, and there is a Bridgestone *story* but no
 * Bridgestone quote, so nothing else here could be meant. A name that matches
 * no card is skipped rather than leaving a hole, so if a Bridgestone card of its
 * own ever lands, adding its name here is the whole change.
 */
const LEAD = ['Litehouse Foods', 'Yazaki', 'LC Waikiki', 'Brisa']

export const TESTIMONIALS: Testimonial[] = [
  ...LEAD.flatMap((company) => IN_ORDER.filter((t) => t.company === company)),
  ...IN_ORDER.filter((t) => !LEAD.includes(t.company)),
]


/** Every slug in `content/stories.ts`, so a card cannot link to a page that is not built. */
const STORY_SLUGS = new Set(STORIES.map((s) => s.slug))

/**
 * Where a quote's story page lives, or nothing.
 *
 * Checked against `STORIES` rather than trusted, because these two catalogues
 * are edited independently and a card whose button 404s is worse than a card
 * with no button. A miss is silent in production and loud in development, the
 * same bargain `Logo.tsx` and `Photo.tsx` make with a missing manifest key.
 */
export function testimonialStoryHref(locale: string, t: Testimonial): string | undefined {
  if (!t.story) return undefined
  if (!STORY_SLUGS.has(t.story)) {
    if (import.meta.env.DEV) console.warn(`no story in the catalogue for ${t.story}`)
    return undefined
  }
  return `/${locale}/${STORIES_INDEX}/${t.story}`
}

/**
 * What this customer said, for their own story page — or nothing.
 *
 * The inverse of `testimonialStoryHref`, and the reason it can live here rather
 * than in `content/stories.ts`: this file already imports that one, so the join
 * belongs on this side of it. A story page reads its quote out of the Hall of
 * Fame instead of restating it, so a customer's words exist once and cannot
 * drift into two versions.
 */
export function testimonialForStory(slug: string): Testimonial | undefined {
  return TESTIMONIALS.find((t) => t.story === slug)
}

/**
 * Every quote that names this story, in wall order.
 *
 * One engagement can have more than one principal: Akkök Holding's story closes
 * on Veysi Küçük for Akkim and Fethi Baytan for Aktek, sent together on
 * 2026-09-04 and asked for as two options the reader switches between. A story
 * with one quote gets an array of one, so `testimonialForStory` above stays what
 * it is for every caller that wants a single card.
 */
export function testimonialsForStory(slug: string): Testimonial[] {
  return TESTIMONIALS.filter((t) => t.story === slug)
}

/**
 * The page's own words, kept; the misattributed quote soup, dropped.
 *
 * `imported/pages/en/testimonials.json` opens with its heading ("Join 300+
 * Happy Companies") and a one-line lede, and everything after those two blocks
 * is the run of mangled quotes these cards replace. The split is by paragraph
 * *length* rather than by index, so an edit or a re-import that adds a sentence
 * to the lede does not silently push a quote into the intro: the lede is ~110
 * characters and the shortest imported quote paragraph is well over twice that.
 *
 * Same job as `splitEbookBlocks` in `content/ebooks.ts`, and the same reason for
 * it — the list is not all of the page.
 */
export function splitTestimonialBlocks(blocks: Block[]): { intro: Block[] } {
  const first = blocks.findIndex((b) => b.kind === 'paragraph' && b.html.length > 200)
  return { intro: first === -1 ? blocks : blocks.slice(0, first) }
}

/**
 * The Hall of Fame's gap, in the shape `build-manifest.json` records copy checks
 * in, so `pnpm verify` refuses a production build while anything about the cards
 * is still unsettled. Without this the amber note would show in a preview and be
 * silently stripped from production, which is the exact failure the check
 * channel exists to prevent.
 *
 * What it says changed on 2026-08-27, from "nine portraits are missing" to "all
 * nineteen are here, somebody who knows these people should look at them once" —
 * the faces were matched to cards from the sender's filenames, and a portrait on
 * the wrong quote is a mistake only a person can see. On 2026-08-28 it gained
 * the resolution split: ten files are now 900×900 and nine are not, which is a
 * batch of files somebody has to send and therefore the same `asset` gap it
 * always was, still one note rather than nine.
 */
export function testimonialChecks(): CheckItem[] {
  return [
    {
      locale: 'en',
      page: 'testimonials',
      section: 'hall-of-fame',
      text: TESTIMONIALS_CHECK,
      // Files somebody has to send, not a claim somebody has to stand behind —
      // see `CheckItem` in content/checks.ts.
      severity: 'asset',
    },
  ]
}

/**
 * The pages whose body is the Hall of Fame cards rather than their own blocks.
 *
 * Same provenance and the same shape as `PAGE_STORIES`, `PAGE_REPORTS` and
 * `PAGE_EBOOKS` — attached in `src/legacy.ts` at load time.
 */
export const PAGE_TESTIMONIALS: Record<string, Testimonial[]> = {
  'en/testimonials': TESTIMONIALS.filter((t) => t.hallOfFame !== false),
}
